// Offline cache for frame data and photos
// Allows the frame to continue displaying and cycling photos when WiFi is off
// Uses localStorage for metadata and IndexedDB for large photo data

import {Platform} from 'react-native';
import {storage} from './storage';
import {FrameData} from '../types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const indexedDB: any;

const CACHE_VERSION = 1;

const CACHE_KEYS = {
  FRAME_DATA: 'oma_frame_cache_data',
  CACHE_TIMESTAMP: 'oma_frame_cache_timestamp',
  CACHE_VERSION: 'oma_frame_cache_version',
  NATIVE_PHOTO_COUNT: 'oma_frame_cache_photo_count',
};

const NATIVE_PHOTO_KEY_PREFIX = 'oma_frame_cache_photo_';

const IDB_NAME = 'oma_offline_cache';
const IDB_STORE = 'photos';
const IDB_VERSION = 1;

// Cache expiry: 7 days
const CACHE_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000;

export interface CachedFrameData {
  frameData: FrameData;
  photoDataUrls: string[];
  timestamp: number;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type IDB = any;

/**
 * Open IndexedDB database (web only)
 */
function openPhotoDB(): Promise<IDB | null> {
  if (Platform.OS !== 'web' || typeof indexedDB === 'undefined') {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    const request = indexedDB.open(IDB_NAME, IDB_VERSION);

    request.onerror = () => {
      console.warn('[OfflineCache] IndexedDB error:', request.error);
      resolve(null);
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event: {target: {result: IDB}}) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE, {keyPath: 'id'});
      }
    };
  });
}

/**
 * Save photos to IndexedDB (web) or AsyncStorage (native, one key per photo)
 */
async function cachePhotos(photoDataUrls: string[]): Promise<void> {
  if (Platform.OS === 'web') {
    const db = await openPhotoDB();
    if (!db) return;

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(IDB_STORE, 'readwrite');
      const store = transaction.objectStore(IDB_STORE);

      // Clear old photos first
      store.clear();

      // Add each photo
      photoDataUrls.forEach((url, index) => {
        store.put({id: index, dataUrl: url});
      });

      transaction.oncomplete = () => {
        db.close();
        resolve();
      };
      transaction.onerror = () => {
        db.close();
        reject(transaction.error);
      };
    });
  } else {
    // Native: store each photo individually to avoid AsyncStorage size limits
    // Clear old photos first
    const oldCountStr = await storage.getItem(CACHE_KEYS.NATIVE_PHOTO_COUNT);
    const oldCount = oldCountStr ? parseInt(oldCountStr, 10) : 0;
    const removePromises = [];
    for (let i = 0; i < oldCount; i++) {
      removePromises.push(
        storage.removeItem(`${NATIVE_PHOTO_KEY_PREFIX}${i}`).catch(() => {}),
      );
    }
    await Promise.all(removePromises);

    // Store each photo separately
    let cachedCount = 0;
    for (let i = 0; i < photoDataUrls.length; i++) {
      try {
        await storage.setItem(`${NATIVE_PHOTO_KEY_PREFIX}${i}`, photoDataUrls[i]);
        cachedCount++;
      } catch (error) {
        console.warn(`[OfflineCache] Failed to cache photo ${i} (likely too large):`, error);
      }
    }

    await storage.setItem(CACHE_KEYS.NATIVE_PHOTO_COUNT, cachedCount.toString());
    console.log(`[OfflineCache] Cached ${cachedCount}/${photoDataUrls.length} photos on native`);
  }
}

/**
 * Load photos from IndexedDB (web) or AsyncStorage (native)
 */
async function loadCachedPhotos(): Promise<string[]> {
  if (Platform.OS === 'web') {
    const db = await openPhotoDB();
    if (!db) return [];

    return new Promise((resolve) => {
      const transaction = db.transaction(IDB_STORE, 'readonly');
      const store = transaction.objectStore(IDB_STORE);
      const request = store.getAll();

      request.onsuccess = () => {
        db.close();
        const photos = request.result
          .sort((a: {id: number}, b: {id: number}) => a.id - b.id)
          .map((item: {id: number; dataUrl: string}) => item.dataUrl);
        resolve(photos);
      };

      request.onerror = () => {
        db.close();
        resolve([]);
      };
    });
  } else {
    // Native: load each photo individually
    try {
      const countStr = await storage.getItem(CACHE_KEYS.NATIVE_PHOTO_COUNT);
      if (!countStr) return [];

      const count = parseInt(countStr, 10);
      if (isNaN(count) || count <= 0) return [];

      const results = await Promise.all(
        Array.from({length: count}, (_, i) =>
          storage.getItem(`${NATIVE_PHOTO_KEY_PREFIX}${i}`).catch(() => null),
        ),
      );
      return results.filter((data): data is string => data !== null);
    } catch {
      return [];
    }
  }
}

/**
 * Save frame data and photos to offline cache
 */
export async function cacheFrameData(
  frameData: FrameData,
  photoDataUrls: string[]
): Promise<void> {
  try {
    const timestamp = Date.now();

    // Store frame metadata (small)
    await storage.setItem(CACHE_KEYS.FRAME_DATA, JSON.stringify(frameData));
    await storage.setItem(CACHE_KEYS.CACHE_TIMESTAMP, timestamp.toString());
    await storage.setItem(CACHE_KEYS.CACHE_VERSION, CACHE_VERSION.toString());

    // Store photos (large)
    await cachePhotos(photoDataUrls);

    console.log(`[OfflineCache] Cached frame data + ${photoDataUrls.length} photos`);
  } catch (error) {
    console.warn('[OfflineCache] Failed to cache frame data:', error);
  }
}

/**
 * Load frame data and photos from offline cache
 */
export async function loadCachedFrameData(): Promise<CachedFrameData | null> {
  try {
    const [frameDataStr, timestampStr, versionStr] = await Promise.all([
      storage.getItem(CACHE_KEYS.FRAME_DATA),
      storage.getItem(CACHE_KEYS.CACHE_TIMESTAMP),
      storage.getItem(CACHE_KEYS.CACHE_VERSION),
    ]);

    if (!frameDataStr || !timestampStr) {
      return null;
    }

    // Check if cache version matches
    const cachedVersion = versionStr ? parseInt(versionStr, 10) : 0;
    if (cachedVersion !== CACHE_VERSION) {
      console.log('[OfflineCache] Cache version mismatch, discarding');
      return null;
    }

    const timestamp = parseInt(timestampStr, 10);

    // Check if cache is expired
    if (Date.now() - timestamp > CACHE_EXPIRY_MS) {
      console.log('[OfflineCache] Cache expired, ignoring');
      return null;
    }

    const frameData = JSON.parse(frameDataStr) as FrameData;
    const photoDataUrls = await loadCachedPhotos();

    // Return frame data even if photos couldn't be cached —
    // reminders, greeting, and time still work without photos
    if (photoDataUrls.length === 0) {
      console.log('[OfflineCache] No cached photos, but returning frame data for reminders');
    } else {
      console.log(`[OfflineCache] Loaded ${photoDataUrls.length} cached photos`);
    }

    return {frameData, photoDataUrls, timestamp};
  } catch (error) {
    console.warn('[OfflineCache] Failed to load cached data:', error);
    return null;
  }
}

/**
 * Clear the offline cache
 */
export async function clearFrameCache(): Promise<void> {
  try {
    await Promise.all([
      storage.removeItem(CACHE_KEYS.FRAME_DATA),
      storage.removeItem(CACHE_KEYS.CACHE_TIMESTAMP),
      storage.removeItem(CACHE_KEYS.CACHE_VERSION),
    ]);

    // Clear photos
    if (Platform.OS === 'web' && typeof indexedDB !== 'undefined') {
      const db = await openPhotoDB();
      if (db) {
        const transaction = db.transaction(IDB_STORE, 'readwrite');
        transaction.objectStore(IDB_STORE).clear();
        db.close();
      }
    } else {
      const countStr = await storage.getItem(CACHE_KEYS.NATIVE_PHOTO_COUNT);
      const count = countStr ? parseInt(countStr, 10) : 0;
      const removePromises = [];
      for (let i = 0; i < count; i++) {
        removePromises.push(
          storage.removeItem(`${NATIVE_PHOTO_KEY_PREFIX}${i}`).catch(() => {}),
        );
      }
      await Promise.all(removePromises);
      await storage.removeItem(CACHE_KEYS.NATIVE_PHOTO_COUNT);
    }

    console.log('[OfflineCache] Cache cleared');
  } catch (error) {
    console.warn('[OfflineCache] Failed to clear cache:', error);
  }
}

/**
 * Check if we have cached data available
 */
export async function hasCachedData(): Promise<boolean> {
  try {
    const timestampStr = await storage.getItem(CACHE_KEYS.CACHE_TIMESTAMP);
    if (!timestampStr) return false;

    const timestamp = parseInt(timestampStr, 10);
    return Date.now() - timestamp < CACHE_EXPIRY_MS;
  } catch {
    return false;
  }
}
